import React, { useMemo, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { AlgoliaDrink, Drink as ReduxDrink } from '@/types/firestore';
import SodaSearchFilterControls from '@/components/sodaSearch/SodaSearchFilterControls';
import { motion, AnimatePresence } from 'framer-motion';
import {
  selectSodaSearchFilters,
  setSortField as setSodaSortField,
  toggleSortDirection as toggleSodaSortDirection,
  searchDrinksWithAlgolia,
  setCurrentPage,
  setItemsPerPage,
  type SodaSortField,
  type SodaDisplayOptionsState
} from '@/store/slices/sodaSearchFiltersSlice';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowDownUp, AlertTriangle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import StarRating from '@/components/ui/star-rating';
import DrinkDetailsModal from '@/components/drinks/DrinkDetailsModal';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { 
  HoverCard, 
  HoverCardContent, 
  HoverCardTrigger 
} from "@/components/ui/hover-card";
import { selectDrinkById } from '@/store/slices/globalDataSlice';

interface DisplayDrink extends AlgoliaDrink {
  brandName: string;
  publicReviewCount?: number;
  flavorNames?: string[];
  attributeNames?: string[];
}

const SORTABLE_FIELDS: { value: SodaSortField; label: string }[] = [
  { value: 'brandName', label: 'Brand Name' },
  { value: 'drinkName', label: 'Drink Name' },
  { value: 'rating', label: 'Rating' },
];

interface SodaItemProps {
  drink: DisplayDrink;
  onSelectDrink: (drinkId: string) => void;
  displayOptions: SodaDisplayOptionsState;
}

const SodaItem: React.FC<SodaItemProps> = ({ drink: algoliaDrink, onSelectDrink, displayOptions }) => {
  const liveDrinkFromStore = useAppSelector(state => selectDrinkById(state, algoliaDrink.objectID)) as ReduxDrink | undefined;

  const displayData: DisplayDrink = {
    ...algoliaDrink,
    ...(liveDrinkFromStore && {
      name: liveDrinkFromStore.name,
      brandName: liveDrinkFromStore.brandName,
      averageRating: liveDrinkFromStore.averageRating,
      reviewCount: liveDrinkFromStore.reviewCount,
      publicReviewCount: liveDrinkFromStore.publicReviewCount,
    })
  };

  const currentFlavorNames = displayData.flavorNames || [];
  const currentAttributeNames = displayData.attributeNames || [];

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          key={algoliaDrink.objectID}
          onClick={() => onSelectDrink(algoliaDrink.objectID)}
          className="block group text-left w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
        >
          <Card className="h-full flex flex-col overflow-hidden gap-2 shadow-sm group-hover:shadow-xl transition-shadow duration-200 group-focus-visible:ring-2 group-focus-visible:ring-primary">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start gap-2">
                <CardTitle className="text-lg font-semibold leading-tight">
                  <span className="text-muted-foreground group-hover:text-muted-foreground/80">{displayData.brandName || 'Unknown Brand'}</span>{' '}
                  <span className="text-primary group-hover:text-primary/80">{displayData.name || 'Unknown Drink'}</span>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="flex-grow flex flex-col space-y-2">
              {(displayOptions.showFlavors && currentFlavorNames.length > 0) || (displayOptions.showAttributes && currentAttributeNames.length > 0) ? (
                <div className="flex flex-wrap gap-1">
                  {displayOptions.showFlavors && currentFlavorNames.slice(0, 3).map((name: string, index: number) => (
                    <Badge key={`${algoliaDrink.objectID}-flavor-${index}`} variant="secondary" className="text-xs">{name}</Badge>
                  ))}
                  {displayOptions.showFlavors && currentFlavorNames.length > 3 && <Badge variant="outline" className="text-xs">+ {currentFlavorNames.length - 3} more</Badge>}
                  
                  {displayOptions.showAttributes && currentAttributeNames.slice(0, 3).map((name: string, index: number) => (
                    <Badge key={`${algoliaDrink.objectID}-attr-${index}`} variant="outline" className="text-xs">{name}</Badge>
                  ))}
                  {displayOptions.showAttributes && currentAttributeNames.length > 3 && <Badge variant="outline" className="text-xs">+ {currentAttributeNames.length - 3} more</Badge>}
                </div>
              ) : null}
              {(displayOptions.showStarRating || displayOptions.showReviewCount) && (
                <div className="flex items-center pt-1">
                  {displayOptions.showStarRating && <StarRating value={typeof displayData.averageRating === 'number' ? displayData.averageRating : 0} readOnly size="sm" />}
                  {displayOptions.showReviewCount && 
                    <span className={`ml-2 text-sm text-muted-foreground ${!displayOptions.showStarRating ? 'ml-0' : ''}`}>
                      ({displayData.reviewCount || 0} ratings)
                      {displayOptions.showReviewCount && typeof displayData.publicReviewCount === 'number' && displayData.publicReviewCount >= 0 && (
                        <span className="ml-1">· {displayData.publicReviewCount} public reviews</span>
                      )}
                    </span>
                  }
                </div>
              )}
            </CardContent>
          </Card>
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 z-50 p-4 bg-secondary text-secondary-foreground shadow-xl rounded-lg border border-border">
        <div className="flex flex-col gap-3">
          {/* Section 1: Title and Rating */}
          <div className="flex justify-between items-start gap-2">
            <div className="flex-grow">
              <h4 className="font-semibold text-lg leading-tight">
                <span className="text-muted-foreground">{displayData.brandName || 'Unknown Brand'}</span>{' '}
                <span className="text-primary">{displayData.name || 'Unknown Drink'}</span>
              </h4>
            </div>
            {typeof displayData.averageRating === 'number' && displayData.averageRating > 0 && (
              <div className="flex-shrink-0 bg-primary text-primary-foreground py-1 px-2.5 rounded-md shadow-md">
                <span className="text-xl font-bold">{displayData.averageRating.toFixed(1)}</span>
              </div>
            )}
          </div>

          {/* Section 2: Flavors and Attributes (Badges) */}
          {((currentFlavorNames && currentFlavorNames.length > 0) || (currentAttributeNames && currentAttributeNames.length > 0)) && (
            <div className="flex flex-wrap gap-1 mt-1">
              {currentFlavorNames.slice(0, 5).map((name: string, index: number) => (
                <Badge key={`hover-flavor-${algoliaDrink.objectID}-${index}`} variant="default" className="text-xs font-normal">{name}</Badge>
              ))}
              {currentFlavorNames.length > 5 && <Badge variant="outline" className="text-xs font-normal">+ {currentFlavorNames.length - 5} more</Badge>}
              
              {currentAttributeNames.slice(0, 5).map((name: string, index: number) => (
                <Badge key={`hover-attr-${algoliaDrink.objectID}-${index}`} variant="outline" className="text-xs font-normal hover:border-primary hover:text-primary">{name}</Badge>
              ))}
              {currentAttributeNames.length > 5 && <Badge variant="outline" className="text-xs font-normal hover:border-primary hover:text-primary">+ {currentAttributeNames.length - 5} more</Badge>}
            </div>
          )}

          {/* Section 3: Fact Box */}
          {((displayData.brandSize && displayData.brandSize !== 'unknown') || typeof displayData.sightingCount === 'number' || typeof displayData.reviewCount === 'number') && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-3 border-t border-border pt-3">
              {displayData.brandSize && displayData.brandSize !== 'unknown' && (
                <>
                  <span className="font-medium text-muted-foreground">Brand Type:</span>
                  <span className="capitalize text-right">{displayData.brandSize}</span>
                </>
              )}
              {typeof displayData.sightingCount === 'number' && (
                <>
                  <span className="font-medium text-muted-foreground">Sightings:</span>
                  <span className="text-right">{displayData.sightingCount}</span>
                </>
              )}
              {typeof displayData.reviewCount === 'number' && (
                <>
                  <span className="font-medium text-muted-foreground">Reviews:</span>
                  <span className="text-right">{displayData.reviewCount}</span>
                </>
              )}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const ITEMS_PER_PAGE_OPTIONS = [6, 12, 24, 36, 48];

const SodaSearchPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const allFilters = useAppSelector(selectSodaSearchFilters);
  const { 
    searchTerm,
    brandSize,
    selectedBrandIds,
    selectedFlavorIds,
    selectedAttributeIds,
    minRating,
    maxRating,
    currentPage,
    sortField, 
    sortDirection, 
    searchResults,
    searchLoading,
    searchError,
    totalResults,
    itemsPerPage,
    displayOptions
  } = allFilters;

  const [activeDrinkId, setActiveDrinkId] = useState<string | undefined>(undefined);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [hasEverHadResults, setHasEverHadResults] = useState(false);
  const [hasEverHadMultiplePages, setHasEverHadMultiplePages] = useState(false);

  useEffect(() => {
    const filtersForThunk: Parameters<typeof searchDrinksWithAlgolia>[0] = {
        query: searchTerm,
        brandSize: brandSize,
        brandIds: selectedBrandIds,
        flavorIds: selectedFlavorIds,
        attributeIds: selectedAttributeIds,
        minRating: minRating,
        maxRating: maxRating,
    };
    dispatch(searchDrinksWithAlgolia(filtersForThunk));
  }, [
    dispatch, 
    searchTerm, 
    brandSize, 
    JSON.stringify(selectedBrandIds),
    JSON.stringify(selectedFlavorIds),
    JSON.stringify(selectedAttributeIds),
    minRating, 
    maxRating,
  ]);

  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [
    searchTerm, 
    brandSize, 
    JSON.stringify(selectedBrandIds),
    JSON.stringify(selectedFlavorIds),
    JSON.stringify(selectedAttributeIds),
    minRating, 
    maxRating,
    currentPage,
    itemsPerPage,
    sortField, 
    sortDirection
  ]);

  useEffect(() => {
    if (searchLoading === 'succeeded') {
      if (totalResults > 0) {
        setHasEverHadResults(true);
      }
    }
  }, [searchLoading, totalResults]);

  const clientSideTotalPages = useMemo(() => {
    if (totalResults === 0 || itemsPerPage === 0) return 0;
    return Math.ceil(totalResults / itemsPerPage);
  }, [totalResults, itemsPerPage]);

  useEffect(() => {
    if (searchLoading === 'succeeded') {
      if (clientSideTotalPages > 1) {
        setHasEverHadMultiplePages(true);
      }
    }
  }, [searchLoading, clientSideTotalPages]);

  const processedDrinks = useMemo(() => {
    const currentSearchResults = Array.isArray(searchResults) ? searchResults : [];
        
    const drinks: DisplayDrink[] = currentSearchResults.map(drink => ({ 
        ...drink,
        brandName: drink.brandName || 'Unknown Brand',
        name: drink.name || 'Unknown Drink',
        flavorNames: drink.flavorNames || [],
        attributeNames: drink.attributeNames || [],
        averageRating: drink.averageRating || 0,
        reviewCount: drink.reviewCount || 0
    }));

    drinks.sort((a, b) => {
      let compareA: string | number | undefined;
      let compareB: string | number | undefined;
      switch (sortField) {
        case 'brandName':
          compareA = a.brandName?.toLowerCase();
          compareB = b.brandName?.toLowerCase();
          break;
        case 'rating':
          compareA = a.averageRating;
          compareB = b.averageRating;
          break;
        case 'drinkName':
        default:
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
          break;
      }
      if (compareA === undefined && compareB === undefined) return 0;
      if (compareA === undefined) return sortDirection === 'asc' ? -1 : 1;
      if (compareB === undefined) return sortDirection === 'asc' ? 1 : -1;
      let comparison = 0;
      if (typeof compareA === 'string' && typeof compareB === 'string') {
        comparison = compareA.localeCompare(compareB);
      } else if (typeof compareA === 'number' && typeof compareB === 'number') {
        if (isNaN(compareA) && isNaN(compareB)) comparison = 0;
        else if (isNaN(compareA)) comparison = sortDirection === 'asc' ? -1 : 1;
        else if (isNaN(compareB)) comparison = sortDirection === 'asc' ? 1 : -1;
        else comparison = compareA - compareB;
      }
      return sortDirection === 'asc' ? comparison : comparison * -1;
    });

    // Client-side pagination
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return drinks.slice(startIndex, endIndex);
  }, [searchResults, sortField, sortDirection, forceUpdate, currentPage, itemsPerPage]);

  const handleSelectDrink = (drinkId: string) => {
    setActiveDrinkId(drinkId);
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };

  const filtersAreDefault = useMemo(() => {
    return (
      allFilters.searchTerm === '' &&
      allFilters.brandSize === 'all' &&
      allFilters.selectedBrandIds.length === 0 &&
      allFilters.selectedFlavorIds.length === 0 &&
      allFilters.selectedAttributeIds.length === 0 &&
      allFilters.minRating === null &&
      allFilters.maxRating === null
    );
  }, [allFilters]);

  const stickyTopOffset = 'top-6';

  const showItemsPerPageControl = (searchLoading === 'succeeded' && totalResults > 0) || (searchLoading === 'pending' && hasEverHadResults);
  const showPaginationControl = (searchLoading === 'succeeded' && clientSideTotalPages > 1) || (searchLoading === 'pending' && hasEverHadMultiplePages);

  return (
    <div className="container mx-auto p-4 md:p-6 h-full flex flex-col max-w-7xl">
      <div className="flex-1 flex flex-col md:flex-row gap-6 md:gap-8 overflow-hidden pt-6">
        <aside className="md:w-80 lg:w-96 flex-shrink-0 relative">
          <div className={`md:sticky ${stickyTopOffset} h-full md:max-h-[calc(100vh-8rem)]`}>
            <SodaSearchFilterControls />
          </div>
        </aside>

        <main className="flex-1 min-w-0 h-full flex flex-col">
          {/* Permanent Controls Section - Always visible */}
          <div className="mb-4 flex-shrink-0">
            {/* Combined Controls Row - Sort, Results count, Items per page, Pagination */}
            <motion.div 
              className="flex flex-col sm:flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 min-h-[40px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Left Part: Sort Controls */}
              <div className="flex gap-2 items-center flex-wrap">
                <Select value={sortField} onValueChange={(value: SodaSortField) => dispatch(setSodaSortField(value))}>
                  <SelectTrigger id="sodaSortField" className="w-auto h-10 text-sm min-w-[150px] bg-card">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SORTABLE_FIELDS.map(field => (
                      <SelectItem key={field.value} value={field.value} className="text-sm">{field.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={() => dispatch(toggleSodaSortDirection())} className="h-10 w-10 flex-shrink-0">
                  <ArrowDownUp size={16} className={sortDirection === 'desc' ? 'rotate-180 transform' : ''} />
                  <span className="sr-only">Toggle Sort Direction</span>
                </Button>
              </div>

              {/* Right Part: Results count, Items per page, Pagination */}
              <div className="flex items-center gap-x-4 gap-y-2 flex-wrap sm:justify-end"> {/* sm:justify-end to push to right on larger screens if space, otherwise natural wrap */}
                <div className="text-sm text-muted-foreground font-medium whitespace-nowrap">
                  {searchLoading === 'pending' ? (
                    <span>Searching...</span>
                  ) : searchLoading === 'succeeded' && totalResults > 0 ? (
                    <span>{totalResults} sodas found</span>
                  ) : searchLoading === 'succeeded' && totalResults === 0 && !filtersAreDefault ? (
                    <span>No sodas match your filters.</span>
                  ) : searchLoading === 'succeeded' && totalResults === 0 && filtersAreDefault ? (
                    <span>No sodas found.</span>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap"> {/* Wrapper for items-per-page and pagination */}
                  <div key="items-per-page-control"> 
                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(value) => {
                        dispatch(setItemsPerPage(parseInt(value, 10)));
                      }}
                      disabled={!showItemsPerPageControl}
                    >
                      <SelectTrigger className="h-10 text-sm w-auto bg-card">
                        <SelectValue placeholder="Items per page" />
                      </SelectTrigger>
                      <SelectContent>
                        {ITEMS_PER_PAGE_OPTIONS.map(option => (
                          <SelectItem key={option} value={option.toString()} className="text-sm">
                            Show {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div key="pagination-control" className="flex justify-center items-center gap-2">
                    <Button 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={!showPaginationControl || currentPage === 0}
                      size="sm"
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {showPaginationControl && clientSideTotalPages > 0 ? `${currentPage + 1} of ${clientSideTotalPages}` : ' '}
                    </span>
                    <Button 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={!showPaginationControl || currentPage >= clientSideTotalPages - 1}
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Content Area - Changes with search results */}
          <div className="flex-grow overflow-y-auto pr-3">
            <AnimatePresence mode="wait">
              {searchLoading === 'pending' && (
                <motion.div 
                  key="loading-state"
                  className="text-center py-10 flex flex-col items-center justify-center h-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <LoadingSpinner />
                  <p className="text-xl text-muted-foreground mt-4">Searching for sodas...</p>
                </motion.div>
              )}
              {searchError && (
                <motion.div 
                  key="error-state"
                  className="text-center py-10 bg-destructive/10 p-4 rounded-md"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-2" />
                  <p className="text-xl text-destructive">Error searching sodas</p>
                  <p className="text-muted-foreground">{searchError}</p>
                </motion.div>
              )}
              {searchLoading === 'succeeded' && processedDrinks.length === 0 && (
                <motion.div 
                  key="empty-state"
                  className="text-center py-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-xl text-muted-foreground">
                    {filtersAreDefault ? 'No sodas found. Try a different search!' : 'No sodas match your current filters.'}
                  </p>
                </motion.div>
              )}
              {searchLoading === 'succeeded' && processedDrinks.length > 0 && (
                <motion.div
                  key="results-state"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 grid-flow-row gap-4 md:gap-6">
                    {processedDrinks.map((drink, index) => (
                      <motion.div
                        key={`${drink.objectID}-page-${currentPage}-update-${forceUpdate}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: index * 0.02 }}
                      >
                        <SodaItem drink={drink} onSelectDrink={handleSelectDrink} displayOptions={displayOptions} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>

      {activeDrinkId && (
        <DrinkDetailsModal drinkId={activeDrinkId} onClose={() => setActiveDrinkId(undefined)} />
      )}
    </div>
  );
};

export default SodaSearchPage; 